import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { get } from 'lodash';
import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { Hint } from '../Hint';

export type ControlledSimpleBooleanSelectProps<T extends FieldValues> = {
  fieldName: FieldPathByValue<T, string | undefined>;
  control: Control<T>;
  hideErrorMessage?: boolean;
  label?: ReactNode;
  hint?: ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  fontSize?: 'u-fs-2' | 'u-fs-3';
  displayValue?: boolean;
};

const OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export function ControlledSimpleBooleanSelect<T extends FieldValues>({
  control,
  fieldName,
  hideErrorMessage = false,
  label,
  hint,
  required,
  disabled,
  fontSize = 'u-fs-3',
  displayValue = false,
}: ControlledSimpleBooleanSelectProps<T>) {
  const {
    field,
    formState: { errors },
  } = useController({ control, name: fieldName });

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}

      {displayValue ? (
        <div className="u-fs-2">{field.value != null ? (field.value ? 'Yes' : 'No') : 'N/A'}</div>
      ) : (
        <>
          <Form.Select
            {...field}
            className={classNames(fontSize, { 'u-border-color-red1': !!get(errors, fieldName) })}
            disabled={disabled}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? undefined : e.target.value === 'true');
            }}
          >
            {OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          {hint && <Hint>{hint}</Hint>}
        </>
      )}

      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
